import { Edge } from '@xyflow/react'

import { IActionSettings, IActionType } from '@makyoapp/makyo-common'

import { SPECIAL_NODES } from '../constants'
import { WorkflowEdge, WorkflowNode, WorkflowNodeData } from '../types'
import { InsertBetweenInfo, WorkflowEdgeManager } from './WorkflowEdgeManager'
import { WorkflowNodeManager } from './WorkflowNodeManager'

export interface WorkflowOperation {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
}

export interface AddNodeOperation {
  newNode: WorkflowNode
  newEdges: WorkflowEdge[]
  removedEdges?: string[]
}

export class WorkflowManager {
  /**
   * Adds a new node to the workflow
   */
  static addNode(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    actionType: IActionType,
    settings: IActionSettings = {},
    sourceNodeId?: string,
    branchInfo?: string | null
  ): AddNodeOperation {
    const newNode = WorkflowNodeManager.createNodeWithGeneratedId(
      actionType,
      settings,
      sourceNodeId ? this.getNewNodePosition(nodes, sourceNodeId) : undefined
    )

    const newEdges: WorkflowEdge[] = []

    if (sourceNodeId) {
      const sourceNode = nodes.find((n) => n.id === sourceNodeId)
      if (sourceNode) {
        const isConditional = WorkflowNodeManager.isConditionalNode(sourceNode)
        const sourceHandle =
          isConditional && branchInfo !== null && branchInfo !== undefined
            ? WorkflowNodeManager.getSourceHandle(sourceNodeId, branchInfo)
            : undefined

        const newEdge = WorkflowEdgeManager.createEdge(sourceNodeId, newNode.id, sourceHandle)
        newEdges.push(newEdge)
      }
    }

    return {
      newNode,
      newEdges,
    }
  }

  /**
   * Inserts a node between two existing nodes
   */
  static insertNodeBetween(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    newNode: WorkflowNode,
    insertInfo: InsertBetweenInfo,
    newNodeActionType?: IActionType
  ): AddNodeOperation {
    // Find the original edge to get the source node
    const originalEdge = edges.find((edge) => edge.id === insertInfo.edgeId)
    if (!originalEdge) {
      throw new Error(`Edge with id ${insertInfo.edgeId} not found`)
    }

    const sourceNodeId = originalEdge.source
    const targetNodeId = originalEdge.target

    const { sourceToNew, newToTarget } = WorkflowEdgeManager.createInsertBetweenEdges(
      sourceNodeId,
      newNode,
      targetNodeId,
      insertInfo,
      newNodeActionType
    )

    return {
      newNode,
      newEdges: [sourceToNew, newToTarget],
      removedEdges: [insertInfo.edgeId],
    }
  }

  /**
   * Updates an existing node
   */
  static updateNode(
    nodes: WorkflowNode[],
    nodeId: string,
    actionType: IActionType,
    settings: IActionSettings = {}
  ): WorkflowNode[] {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return WorkflowNodeManager.updateNodeData(node, actionType, settings)
      }
      return node
    })
  }

  /**
   * Removes a node and all its connected edges
   */
  static removeNode(nodes: WorkflowNode[], edges: WorkflowEdge[], nodeId: string): WorkflowOperation {
    const updatedNodes = nodes.filter((node) => node.id !== nodeId)
    const updatedEdges = WorkflowEdgeManager.removeNodeEdges(edges, nodeId)

    return {
      nodes: updatedNodes,
      edges: updatedEdges,
    }
  }

  /**
   * Removes a specific edge
   */
  static removeEdge(edges: WorkflowEdge[], edgeId: string): WorkflowEdge[] {
    return WorkflowEdgeManager.removeEdge(edges, edgeId)
  }

  static removeEdgesByWhiteListBranchIds(edges: WorkflowEdge[], nodeId: string, branchIds: string[]): WorkflowEdge[] {
    return WorkflowEdgeManager.removeEdgesByWhiteListBranchIds(edges, nodeId, branchIds)
  }

  /**
   * Creates initial edges from workflow nodes
   */
  static createInitialEdges(nodes: WorkflowNode[]): WorkflowEdge[] {
    const edges: WorkflowEdge[] = []

    nodes.forEach((node) => {
      const nodeData = node.data

      if (nodeData.actionValue.id === SPECIAL_NODES.trigger) {
        // Trigger node - connect to the first actual action
        const firstAction = nodes.find((n) => {
          const nData = n.data
          return nData.actionValue.id !== SPECIAL_NODES.trigger
        })
        if (firstAction) {
          edges.push(WorkflowEdgeManager.createEdge(node.id, firstAction.id))
        }
        return
      }

      // Handle nextAction based on its type
      const nextAction = nodeData.actionValue.nextAction
      if (typeof nextAction === 'string') {
        // Simple string reference
        const targetNode = nodes.find((n) => n.id === nextAction)
        if (targetNode) {
          edges.push(WorkflowEdgeManager.createEdge(node.id, targetNode.id))
        }
      } else if (Array.isArray(nextAction)) {
        // IActionStep array - create edges for each branch
        nextAction.forEach((step, index) => {
          const targetNode = nodes.find((n) => n.id === step.actionId)
          if (targetNode) {
            const isConditional = WorkflowNodeManager.isConditionalNode(node)
            let sourceHandle: string | undefined
            let edgeStartLabel: string | undefined

            if (isConditional) {
              if (nodeData.actionValue.type === IActionType.ConditionalIf) {
                // For ConditionalIf, determine if this is true or false branch
                sourceHandle = step.default ? `${node.id}-next-action-false` : `${node.id}-next-action-true`
              } else if (nodeData.actionValue.type === IActionType.ConditionalSwitch) {
                const currentCase = nodeData.actionValue.settings?.cases?.find(
                  (caseItem: any) => caseItem.actionId === step.actionId
                )
                const branchIdBasedOnActionId = currentCase?.id ?? nodeData.actionValue.settings?.cases?.[index]?.id
                sourceHandle = WorkflowNodeManager.getSourceHandle(node.id, branchIdBasedOnActionId)
                edgeStartLabel = currentCase?.name
              } else if (nodeData.actionValue.type === IActionType.AddToCart) {
                sourceHandle = step.default
                  ? WorkflowNodeManager.getSourceHandle(node.id, 'outsideCart')
                  : WorkflowNodeManager.getSourceHandle(node.id, 'insideCart')
              }
            }

            edges.push(
              WorkflowEdgeManager.createEdgeWithId(
                `${node.id}-${targetNode.id}-${index}`,
                node.id,
                targetNode.id,
                sourceHandle
              )
            )
          }
        })
      }
    })

    return edges
  }

  /**
   * Validates if a connection is valid
   */
  static isValidConnection(edges: Array<WorkflowEdge | Edge>, targetNodeId: string): boolean {
    return WorkflowEdgeManager.isValidConnection(edges, targetNodeId)
  }

  /**
   * Gets the position for a new node based on source node
   */
  private static getNewNodePosition(nodes: WorkflowNode[], sourceNodeId: string): { x: number; y: number } {
    const sourceNode = nodes.find((n) => n.id === sourceNodeId)
    if (sourceNode) {
      return WorkflowNodeManager.getNewNodePosition(sourceNode)
    }
    return { x: 0, y: 0 }
  }

  /**
   * Checks if a node has outgoing connections
   */
  static hasOutgoingConnections(edges: WorkflowEdge[], nodeId: string): boolean {
    return WorkflowEdgeManager.hasOutgoingConnections(edges, nodeId)
  }

  /**
   * Gets all outgoing edges for a node
   */
  static getOutgoingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
    return WorkflowEdgeManager.getOutgoingEdges(edges, nodeId)
  }

  /**
   * Gets all incoming edges for a node
   */
  static getIncomingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
    return WorkflowEdgeManager.getIncomingEdges(edges, nodeId)
  }

  /**
   * Finds edges for a specific branch of a conditional node
   */
  static findBranchEdges(edges: WorkflowEdge[], nodeId: string, branch: string): WorkflowEdge[] {
    return WorkflowEdgeManager.findBranchEdges(edges, nodeId, branch)
  }

  static addActionIdToSwitchActionCase(
    nodes: WorkflowNode[],
    actionIdToUpdate: string,
    caseId: string,
    actionId: string
  ): WorkflowNode[] {
    return WorkflowNodeManager.addActionIdToSwitchActionCase(nodes, actionIdToUpdate, caseId, actionId)
  }

  static removeActionIdFromSwitchActionCase(
    nodes: WorkflowNode[],
    actionIdToUpdate: string,
    actionIdToRemove: string
  ): WorkflowNode[] {
    return WorkflowNodeManager.removeActionIdFromSwitchActionCase(nodes, actionIdToUpdate, actionIdToRemove)
  }
}
